// components/session/SessionManagement/index.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { Calendar, Clock, X, RefreshCcw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { 
    fetchUserSessions,
    cancelSession,
    selectUserSessions,
    selectSessionsLoading,
    selectSessionError
} from '../../../store/slices/sessionSlice';
import { Button } from '../../../styles/components';
import RescheduleModal from './RescheduleModal';
import {
    SessionsContainer,
    SessionsHeader,
    TabList,
    TabButton,
    SessionsList,
    SessionCard,
    SessionInfo,
    SessionActions,
    StatusBadge,
    NoSessions,
    LoadingSpinner
} from './styles';
import useAuth from '../../../hooks/useAuth';

const SessionManagement = () => {
    const dispatch = useDispatch();
    const { user } = useAuth();
    const sessions = useSelector(selectUserSessions);
    const loading = useSelector(selectSessionsLoading);
    const error = useSelector(selectSessionError);

    
    const [activeTab, setActiveTab] = useState('upcoming');
    const [selectedSession, setSelectedSession] = useState(null);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    
    const initialFetchDone = useRef(false); 

    useEffect(() => {
        if (user?.uid) {
            dispatch(fetchUserSessions(user.uid));
        }
    }, [dispatch, user?.uid]); 

    if (error) {
        toast.error(error);
    }

    const getFilteredSessions = () => {
        const now = new Date();
        
        switch (activeTab) {
            case 'upcoming':
                return sessions.filter(session => 
                    new Date(session.date.toDate()) > now && 
                    session.status !== 'cancelled'
                );
            case 'completed':
                return sessions.filter(session => 
                    session.status === 'completed'
                );
            case 'cancelled':
                return sessions.filter(session => 
                    session.status === 'cancelled'
                );
            default:
                return sessions;
        }
    };

    const handleCancel = async (session) => {
        if (window.confirm('Are you sure you want to cancel this session?')) {
            try {
                await dispatch(cancelSession(session.id)).unwrap();
                toast.success('Session cancelled successfully');
            } catch (error) {
                toast.error(error.message || 'Failed to cancel session');
            }
        }
    };

    const handleReschedule = (session) => {
        setSelectedSession(session);
        setShowRescheduleModal(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'confirmed':
                return 'success';
            case 'completed':
                return 'success';
            case 'cancelled':
                return 'error';
            case 'rescheduled':
                return 'info';
            default:
                return 'default';
        }
    };

    if (loading && sessions.length === 0) {
        return (
            <SessionsContainer>
                <LoadingSpinner />
            </SessionsContainer>
        );
    }

    return (
        <SessionsContainer>
            <SessionsHeader>
                <h2>Your Sessions</h2>
                <TabList>
                    <TabButton 
                        active={activeTab === 'upcoming'}
                        onClick={() => setActiveTab('upcoming')}
                    >
                        Upcoming
                    </TabButton>
                    <TabButton 
                        active={activeTab === 'completed'}
                        onClick={() => setActiveTab('completed')}
                    >
                        Completed
                    </TabButton>
                    <TabButton 
                        active={activeTab === 'cancelled'}
                        onClick={() => setActiveTab('cancelled')}
                    >
                        Cancelled
                    </TabButton>
                </TabList>
            </SessionsHeader>

            <SessionsList>
                {getFilteredSessions().map(session => (
                    <SessionCard key={session.id}>
                        <SessionInfo>
                            <div className="primary-info">
                                <div className="date-time">
                                    <span className="icon"><Calendar size={16} /></span>
                                    <span className="text">
                                        {format(session.date.toDate(), 'PPP')}
                                    </span>
                                </div>
                                <div className="date-time">
                                    <span className="icon"><Clock size={16} /></span>
                                    <span className="text">{session.timeSlot}</span>
                                </div>
                                <StatusBadge status={getStatusColor(session.status)}>
                                    {session.status}
                                </StatusBadge>
                            </div>
                            
                            {session.notes && (
                                <div className="notes">
                                    <p>{session.notes}</p>
                                </div>
                            )}
                        </SessionInfo>

                        {session.status === 'pending' && (
                            <SessionActions>
                                <Button 
                                    variant="outlined"
                                    onClick={() => handleCancel(session)}
                                >
                                    <X size={16} />
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => handleReschedule(session)}
                                >
                                    <RefreshCcw size={16} />
                                    Reschedule
                                </Button>
                            </SessionActions>
                        )}
                    </SessionCard>
                ))}

                {getFilteredSessions().length === 0 && (
                    <NoSessions>
                        <AlertCircle size={48} />
                        <h3>No {activeTab} sessions</h3>
                        {activeTab === 'upcoming' && (
                            <p>Book your first session now!</p>
                        )}
                    </NoSessions>
                )}
            </SessionsList>

            {showRescheduleModal && selectedSession && (
                <RescheduleModal 
                    session={selectedSession}
                    onClose={() => {
                        setShowRescheduleModal(false);
                        setSelectedSession(null);
                    }}
                />
            )}
        </SessionsContainer>
    );
};

export default SessionManagement;